import moment from "moment";
import React, { useContext, useEffect, useRef } from "react";
import { Button, Dropdown, Table, Form } from "react-bootstrap";
import checkPermissions from "@beeldit/user-and-access/auth/services/checkPermissions";
import checkRoles from "@beeldit/user-and-access/auth/services/checkRoles";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { FinalTableConfig } from "../models/FinalTableConfig.interface";
import { User } from "@beeldit/user-and-access/users/models/User";

import { useTranslation } from "react-i18next";
import { t } from "i18next";

import "../scss/BeelditTable.css";
import {
  CaretDown,
  CaretUp,
  CaretUpDown,
  Check,
  X,
  DotsThreeVertical,
} from "@phosphor-icons/react";
import { BulkActionActionContext } from "../context/BulkActionContext";

interface Props {
  config: FinalTableConfig;
  data: {
    elements: any[];
    totalElements: number;
  };
  orderBy?: any;
  onOrderByChange?: any;
}

function BeelditTable(props: Props) {
  const { columns, rowActions, rowConfiguration, bulkActions } = props.config;
  const { elements } = props.data;
  const { orderBy, onOrderByChange } = props;

  const { user } = useContext(AuthContext);
  const { t } = useTranslation();

  const bulkActionContext = useContext<any>(BulkActionActionContext);
  const { elementIds, setElementIds } = bulkActionContext || {};
  const selectAllRef = useRef<HTMLInputElement>(null);
  
  // Verificar si hay bulkActions habilitadas
  const hasBulkActions = !!(bulkActions && bulkActions.length > 0 && bulkActions.some((action: any) => action.allowed && action.show));
  // Actualiza el estado `indeterminate` del checkbox de selección total
  useEffect(() => {
    if (!elementIds || !hasBulkActions) return;
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        elementIds.length > 0 && elementIds.length < elements.length;
    }
  }, [elementIds, elements.length, hasBulkActions]);

  return (
    <div>
      <Table hover responsive className="table-sm">
        <thead>
          <tr>
            {/* Checkbox de selección total - solo si hay bulkActions */}
            {hasBulkActions && (
                <th style={{ width: '40px', minWidth: '40px', maxWidth: '40px', textAlign: 'center', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}>
                  <Form.Check
                    type="checkbox"
                    ref={selectAllRef}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setElementIds(elements.map((el) => el.id));
                      } else {
                        setElementIds([]);
                      }
                    }}
                    checked={elementIds && elements && elementIds.length === elements.length && elements.length > 0}
                  />
                </th>
            )}
            {user ? (
              <>
                {columns.map((column) => {
                  if (
                    (!column.permissions ||
                      checkPermissions(user, column.permissions)) &&
                    (!column.roles || checkRoles(user, column.roles)) &&
                    checkShow(column.show, null, user)
                  ) {
                    let className = "text-uppercase " + column.type;
                    return (
                      <th className={className} key={column.name}>
                        {column.allowOrder ? (
                          <>
                            {orderBy === "-" + column.name ? (
                              <CaretDown
                                size={12}
                                className="me-1 mb-1 click"
                                onClick={() => {
                                  onOrderBy(
                                    column.name,
                                    orderBy,
                                    onOrderByChange
                                  );
                                }}
                              />
                            ) : null}
                            {orderBy === "+" + column.name ? (
                              <CaretUp
                                size={12}
                                className="me-1 mb-1 click"
                                onClick={() => {
                                  onOrderBy(
                                    column.name,
                                    orderBy,
                                    onOrderByChange
                                  );
                                }}
                              />
                            ) : null}
                            {orderBy !== "+" + column.name &&
                            orderBy !== "-" + column.name ? (
                              <CaretUpDown
                                size={16}
                                className="me-1 mb-1 click"
                                onClick={() => {
                                  onOrderBy(
                                    column.name,
                                    orderBy,
                                    onOrderByChange
                                  );
                                }}
                              />
                            ) : null}
                          </>
                        ) : null}
                        {t(column.label)}
                      </th>
                    );
                  }
                  return null;
                })}
              </>
            ) : null}
            <th className="action-column text-uppercase">{t("Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {renderElements(
            elements,
            columns,
            rowActions,
            rowConfiguration,
            user,
            elementIds,
            setElementIds,
            hasBulkActions
          )}
        </tbody>
      </Table>
    </div>
  );
}

function renderElements(
  elements: any[],
  columns: any[],
  rowActions: any[],
  rowConfiguration: any,
  user: User | null,
  elementIds: string[],
  setElementIds: React.Dispatch<React.SetStateAction<string[]>>,
  hasBulkActions: boolean
): any {
  return elements.map((element) => {
    if (!elementIds) return null;
    const isSelected = elementIds.includes(element.id);
    return (
      <tr
        className={`${rowConfiguration.class(element)} ${
          isSelected ? "selected-row" : ""
        }`}
        key={element.id}
      >
        {/* Checkbox por fila - solo si hay bulkActions */}
        {hasBulkActions && (
          <td style={{ width: '40px', minWidth: '40px', maxWidth: '40px', textAlign: 'center', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 9 }}>
            <Form.Check
              type="checkbox"
              checked={isSelected}
              onChange={() => {
                setElementIds((prev) =>
                  isSelected
                    ? prev.filter((id) => id !== element.id)
                    : [...prev, element.id]
                );
              }}
            />
          </td>
        )}
        {renderElement(element, columns, rowActions, user)}
      </tr>
    );
  });
}


function onOrderBy(label: string, orderBy: string, onOrderByChange: any) {
  let newOrderBy;
  if (orderBy) {
    const direction = orderBy.substring(0, 1);
    const labelToOrder = orderBy.substring(1);
    if (label === labelToOrder) {
      newOrderBy = direction === "+" ? "-" + label : "+" + label;
    } else {
      newOrderBy = "+" + label;
    }
  } else {
    newOrderBy = "+" + label;
  }
  onOrderByChange(newOrderBy);
}

function renderElement(
  element: any,
  columns: any[],
  rowActions: any[],
  user: User | null
): any {
  return columns
    .map((column) => {
      if (
        (!column.permissions || checkPermissions(user, column.permissions)) &&
        (!column.roles || checkRoles(user, column.roles))
      ) {
        let className = "ps-2 " + column.type;
        return (
          <td key={column.name} className={className}>
            {getFieldValue(element, column.key, column)}
          </td>
        );
      }
      return null;
    })
    .concat(renderRowActions(rowActions, element, user));
}

function renderRowActions(
  rowActions: any[],
  element: any,
  user: User | null
): any {
  return (
    <td key="action" className="actions-buttons text-right">
      {rowActions.map((rowAction) => {
        if (
          checkPermissions(user, rowAction.permissions) &&
          checkRoles(user, rowAction.roles) &&
          checkShow(rowAction.show, element, user)
        ) {
          return (
            // Si rowAction no tiene actions se muestra el botón
            !rowAction.actions ? (
              <Button
                className="icon-button rounded-circle button-action btn-action btn btn-primary"
                key={rowAction.label}
                onClick={() =>
                  rowAction.action(getActionData(rowAction, element))
                }
              >
                {rowAction.icon}
              </Button>
            ) : (
              // Si rowActions tiene actions mostramos el botón con el icon y el dropdown. En el dropdown se muestran los actions
              <Dropdown key={rowAction.label}>
                <Dropdown.Toggle
                  as={Button}
                  variant="link"
                  id="dropdown-basic"
                  className="icon-button rounded-circle button-action btn-action btn btn-primary p-0"
                  bsPrefix="custom-dropdown-toggle"
                >
                  {rowAction.icon || <DotsThreeVertical size={20} />}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {rowAction.actions.map((action: any) => {
                  if (
                      checkPermissions(user, action.permissions) &&
                      checkRoles(user, action.roles) &&
                      checkShow(action.show, element, user)
                    ) {
                    return (
                      <Dropdown.Item
                        key={action.label}
                        onClick={() =>
                          action.action(getActionData(action, element))
                        }
                      >
                        {action.icon}
                        <span style={{ marginLeft: '8px' }}>{t(action.label)}</span>
                      </Dropdown.Item>
                    );
                  }
                  return null;
                  })}
                </Dropdown.Menu>
              </Dropdown>
            )
          );
        }
        return null;
      })}
    </td>
  );
}

function getFieldValue(element: any, key: string, column: any): any {
  const keys = key.split(".");

  for (let key of keys) {
    if (element[key] === undefined || element[key] == null) {
      return null;
    }
    element = element[key];
  }
  if (column.type === "boolean") {    
    return element ? (column.true_icon ? column.true_icon : <Check className={column.class} size={22} />) : (column.show_false ? <X size={22} /> : '');
  }

  if (column.type === "url") {
    if (element.includes("http")) {
      return (
        <a target="_blank" rel="noreferrer" href={element}>
          {element}
        </a>
      );
    }
  }

  if (column.type === "currency" && column.currency) {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: column.currency,
    }).format(element);
  }
  if (column.type === "array") {
    return element.map((el: any, idx: number) => {
      return (
        <span key={`${column.name || column.key}-${idx}`}>
          {el[column.attribute]}
          <br />
        </span>
      );
    });
  }

  if (column.type === "array-first-element") {
    if (element.length) {
      return column.attribute.split(".").reduce((obj: any, key: any) => {
        return (
          obj && element[0][obj] && element[0][obj][key], element[0][obj][key]
        );
      });
    }
    return "";
  }

  element = format(element, column);
  return element;
}

function format(value: any, column: any): any {
  if (column.type === "date") {
    value = moment(value, "YYYY-MM-DD");
    return value.format("DD/MM/YYYY");
  } else if (column.type === "datetime") {
    value = moment(value);
    return value.format("DD/MM/YYYY HH:mm");
  } else if (column.type === "translation") {
    const prefix = column.translation_prefix;
    value = t(prefix + "_" + value);
    return value;
  } else if (column.type === "percentage") {
    value = value + "%";
    return value;
  }
  return value;
}

function getActionData(action: any, element: any): any {
  let data = action.data === undefined ? "id" : action.data;

  if (data) {
    data = data.split(".");

    for (let key of data) {
      if (element[key] === undefined || !element[key]) {
        return null;
      }

      element = element[key];
    }
  }

  return element;
}

function checkShow(show: any, element: any, user: any): boolean {
  if (typeof show === "boolean") {
    return show;
  } else if (typeof show === "function") {
    const res = show(element, user);
    return res;
  }
  return false;
}

export default BeelditTable;
